import { CommercialClientTermResponse } from '@interfaces/partners/responses/commercial-client-term.response';
import {
  PARTNER_VIEW_STAGE_LABELS,
  PARTNER_VISIBLE_DEAL_STAGES,
  PartnerDealResponse,
  PartnerDealStage,
  partnerDealStatusLabel,
  toPartnerVisibleStage,
} from '@interfaces/partners/responses/partner-deal.response';
import { resolverDependeDeVoce } from '@features/dashboard/commercial/pages/deal-detail/deal-presenter';
import { PartnerEarningsResponse } from '@interfaces/partners/responses/partner-flat-fee.response';

/**
 * Como o Início do parceiro (card 3517) **conta** a carteira: números do topo, funil por etapa e
 * a lista "O que depende de você agora".
 *
 * Funções puras, sem injeção: recebem os deals do board (`GET partners/deals`) e os termos dos
 * deals em Consentimento, e devolvem o que a tela desenha. Nada aqui é cálculo financeiro, só
 * contagem e ordenação de exibição.
 *
 * ⚠️ A pendência de termo usa a **mesma** regra da faixa "Depende de você" da tela do deal
 * (`resolverDependeDeVoce`). Duas regras para a mesma pergunta acabariam discordando: o Início
 * pedindo para enviar um termo que a tela do deal diz que já foi assinado.
 */

/** Reserva com este prazo ou menos vira urgência (o mesmo corte do protótipo v2). */
export const RESERVATION_WARNING_DAYS = 20;

export type PendingUrgency = 'high' | 'medium' | 'low';

export interface PendingItem {
  dealId: string;
  corporateName: string;
  /** O que o parceiro precisa fazer, em português. */
  action: string;
  /** Linha de contexto (prazo da reserva, assinaturas). */
  detail: string;
  /** Texto do botão. */
  cta: string;
  urgency: PendingUrgency;
}

export interface FunnelBar {
  stage: PartnerDealStage;
  label: string;
  count: number;
  /** Largura da barra em %, proporcional ao maior valor; 0 na etapa vazia (card 3745: barra deitada). */
  percent: number;
}

export interface PartnerHomeSummary {
  activeCount: number;
  totalCount: number;
  closedCount: number;
  pendingCount: number;
  activeReservations: number;
  /** Reservas ativas que vencem em até `RESERVATION_WARNING_DAYS` dias. */
  expiringReservations: number;
  /** Deals ativos sem nenhuma ação do parceiro: a bola está com a Legatus. */
  withLegatusCount: number;
}

/** Filtro da lista "Seus deals". */
export type DealListFilter = 'all' | 'active' | 'rejected';

/** Uma linha da lista "Seus deals" (card 3558): Deal › etapa › situação. */
export interface DealListRow {
  dealId: string;
  corporateName: string;
  /** Etapa como o parceiro a vê (sem Gates). Num rejeitado, a etapa onde ele parou. */
  stageLabel: string;
  /** A mesma frase da pílula do board ("Em comitê", "Aguardando acordo", "Rejeitado"). */
  status: string;
  rejected: boolean;
  rejectedAt: string | null;
  amount: number;
  currencyCode: string;
}

/** Etapa com deal nunca fica com barra menor que isto, para não sumir ao lado da maior. */
const FUNNEL_MIN_PERCENT = 6;

/** Rótulo curto da etapa no eixo do funil, onde o nome inteiro não cabe. */
const SHORT_STAGE_LABELS: Partial<Record<PartnerDealStage, string>> = {
  // Rótulo do parceiro "NDA e Bacen" (card 3612), encurtado.
  Consentimento: 'NDA',
  Documentacao: 'Docs',
  // "Contrato enviado" e "Contrato assinado" desde o card 3707.
  Contratacao: 'Enviado',
  Operacao: 'Assinado',
};

const URGENCY_ORDER: Record<PendingUrgency, number> = { high: 0, medium: 1, low: 2 };

/** Deal ainda em andamento. Rejeitado não é etapa, mas encerra o deal para o Início. */
export function isActiveDeal(deal: PartnerDealResponse): boolean {
  return !deal.rejectedAt;
}

/**
 * Funil do parceiro: uma barra por etapa visível, na ordem do board, contando só os deals ativos.
 *
 * Usa `toPartnerVisibleStage` como o board: o backend já projeta a etapa interna, isto é defesa.
 */
export function buildFunnel(deals: PartnerDealResponse[]): FunnelBar[] {
  const active = deals.filter(isActiveDeal);
  const counts = PARTNER_VISIBLE_DEAL_STAGES.map(
    stage => active.filter(deal => toPartnerVisibleStage(deal.stage) === stage).length
  );
  const max = Math.max(1, ...counts);

  return PARTNER_VISIBLE_DEAL_STAGES.map((stage, index) => ({
    stage,
    label: SHORT_STAGE_LABELS[stage] ?? PARTNER_VIEW_STAGE_LABELS[stage],
    count: counts[index],
    percent: counts[index]
      ? Math.max(FUNNEL_MIN_PERCENT, Math.round((counts[index] / max) * 100))
      : 0,
  }));
}

/** Texto do prazo da reserva, ou nulo quando ela já é definitiva. */
export function describeReservation(daysLeft: number | null): string | null {
  if (daysLeft === null) return null;
  if (daysLeft < 0) return 'reserva vencida';
  if (daysLeft === 0) return 'reserva vence hoje';
  if (daysLeft === 1) return 'reserva vence amanhã';
  return `reserva vence em ${daysLeft} dias`;
}

/**
 * O que depende do parceiro agora, por urgência.
 *
 * Três fontes, todas com dado que a plataforma já tem:
 * - **Termo** (Consentimento): enviar o NDA + carta SCR ou reforçar as assinaturas, pela régua da
 *   tela do deal.
 * - **Documento com falha**: a leitura de um arquivo falhou e ele precisa ser reenviado.
 * - **Reserva vencendo** sem outra pendência: o primeiro documento torna a reserva definitiva.
 *
 * Urgência alta quando a reserva vence em até `RESERVATION_WARNING_DAYS` dias: perder a reserva é
 * perder o cliente para outro parceiro.
 *
 * @param deals Deals do board.
 * @param terms Termo de cada deal em Consentimento (`null` = ainda não existe termo).
 * @param daysLeftOf Dias restantes da reserva do deal (nulo = reserva definitiva).
 */
export function buildPendingItems(
  deals: PartnerDealResponse[],
  terms: ReadonlyMap<string, CommercialClientTermResponse | null>,
  daysLeftOf: (deal: PartnerDealResponse) => number | null
): PendingItem[] {
  const items: PendingItem[] = [];

  for (const deal of deals.filter(isActiveDeal)) {
    const daysLeft = daysLeftOf(deal);
    const reservation = describeReservation(daysLeft);
    const reservationIsUrgent = daysLeft !== null && daysLeft <= RESERVATION_WARNING_DAYS;
    const before = items.length;

    if (deal.stage === 'Consentimento') {
      const term = terms.get(deal.id) ?? null;
      const termInProgress = isTermInProgress(term);
      const action = resolverDependeDeVoce(deal, term?.status, termInProgress);

      if (action) {
        const pending = (term?.signatories ?? []).filter(s => !s.signedAt).length;
        items.push({
          dealId: deal.id,
          corporateName: deal.corporateName,
          action: termInProgress && pending > 0 ? `${action} (${pendingLabel(pending)})` : action,
          detail: joinDetail(
            termInProgress ? 'Termo já enviado ao cliente' : 'Termo ainda não enviado',
            reservation
          ),
          cta: termInProgress ? 'Reforçar' : 'Enviar',
          urgency: reservationIsUrgent ? 'high' : termInProgress ? 'low' : 'medium',
        });
      }
    }

    if (deal.documentStatus === 'Failed') {
      items.push({
        dealId: deal.id,
        corporateName: deal.corporateName,
        action: 'Reenviar o documento que não pôde ser lido',
        detail: joinDetail('A leitura de um arquivo falhou', reservation),
        cta: 'Reenviar',
        urgency: reservationIsUrgent ? 'high' : 'medium',
      });
    }

    if (items.length === before && reservationIsUrgent) {
      items.push({
        dealId: deal.id,
        corporateName: deal.corporateName,
        action: 'Enviar o primeiro documento para confirmar a reserva do CNPJ',
        detail: reservation ?? '',
        cta: 'Abrir deal',
        urgency: 'high',
      });
    }
  }

  return items.sort((a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency]);
}

/** Números do topo do Início. */
export function buildSummary(
  deals: PartnerDealResponse[],
  pending: PendingItem[],
  daysLeftOf: (deal: PartnerDealResponse) => number | null
): PartnerHomeSummary {
  const active = deals.filter(isActiveDeal);
  const reservations = active
    .map(daysLeftOf)
    .filter((days): days is number => days !== null && days >= 0);
  const dealsWithPending = new Set(pending.map(item => item.dealId));

  return {
    activeCount: active.length,
    totalCount: deals.length,
    closedCount: deals.length - active.length,
    pendingCount: pending.length,
    activeReservations: reservations.length,
    expiringReservations: reservations.filter(days => days <= RESERVATION_WARNING_DAYS).length,
    withLegatusCount: active.filter(deal => !dealsWithPending.has(deal.id)).length,
  };
}

/** Deals cujo termo precisa ser lido para saber se há pendência. */
export function dealsNeedingTerm(deals: PartnerDealResponse[]): PartnerDealResponse[] {
  return deals.filter(deal => isActiveDeal(deal) && deal.stage === 'Consentimento');
}

/**
 * Termo que existe **no provedor**, como na tela do deal: `Pendente` é "ainda não enviado" e
 * `Cancelado` não conta como enviado.
 */
function isTermInProgress(term: CommercialClientTermResponse | null): boolean {
  const status = term?.status;
  return !!status && status !== 'Pendente' && status !== 'Cancelado';
}

function pendingLabel(count: number): string {
  return count === 1 ? '1 assinatura pendente' : `${count} assinaturas pendentes`;
}

function joinDetail(...parts: (string | null)[]): string {
  return parts.filter(Boolean).join(' · ');
}

/**
 * Lista "Seus deals" do Início (card 3558): todos os deals do parceiro, inclusive os rejeitados.
 *
 * Ordem: ativos primeiro, do mais avançado no funil para o menos (é o que está mais perto de virar
 * operação), e dentro da etapa o mais novo primeiro; rejeitados no fim, o mais recente primeiro. O
 * board não tem data da última movimentação, então a etapa é o melhor sinal de "onde está a ação".
 *
 * @param deals Deals do board (`GET partners/deals`).
 * @param filter Todos, só ativos ou só rejeitados.
 */
export function buildDealList(
  deals: PartnerDealResponse[],
  filter: DealListFilter = 'all'
): DealListRow[] {
  const stageIndex = (deal: PartnerDealResponse) =>
    PARTNER_VISIBLE_DEAL_STAGES.indexOf(toPartnerVisibleStage(deal.stage));
  const byDateDesc = (a: string | null | undefined, b: string | null | undefined) =>
    (b ?? '').localeCompare(a ?? '');

  const active = deals
    .filter(isActiveDeal)
    .sort((a, b) => stageIndex(b) - stageIndex(a) || byDateDesc(a.createdAt, b.createdAt));
  const rejected = deals
    .filter(deal => !isActiveDeal(deal))
    .sort((a, b) => byDateDesc(a.rejectedAt, b.rejectedAt));

  const ordered =
    filter === 'active' ? active : filter === 'rejected' ? rejected : [...active, ...rejected];

  return ordered.map(deal => {
    const stage = toPartnerVisibleStage(deal.stage);
    return {
      dealId: deal.id,
      corporateName: deal.corporateName,
      stageLabel: PARTNER_VIEW_STAGE_LABELS[stage] ?? stage,
      status: partnerDealStatusLabel(deal, stage),
      rejected: !!deal.rejectedAt,
      rejectedAt: deal.rejectedAt ?? null,
      amount: deal.amount,
      currencyCode: deal.currencyCode,
    };
  });
}

/** Soma de uma moeda. */
export interface CurrencyTotal {
  currency: string;
  total: number;
}

/** Volume e fee estimado dos deals ativos (card 3707), prontos para o template. */
export interface PartnerEarnings {
  /** Soma do valor pretendido dos deals ativos, por moeda (BRL primeiro). */
  volume: CurrencyTotal[];
  /** Fee estimado do parceiro, por moeda: só as moedas com algum deal com prazo. */
  estimatedFee: CurrencyTotal[];
  /** Deals ativos sem prazo da captação: entram no volume, mas não no fee (sem prazo não há flat fee). */
  dealsWithoutTerm: number;
}

/**
 * Volume das captações e fee estimado do parceiro (card 3707, item 1 do "melhorias painel do parceiro 2"), no formato do
 * template. A conta inteira é do backend (`GET partners/me/earnings`, `PartnerFlatFeeCalculator`): aqui só se
 * reorganiza a resposta. Sem resposta (carregando ou falhou), tudo vazio.
 * @param response Resposta de `me/earnings`, ou nula.
 */
export function toPartnerEarnings(response: PartnerEarningsResponse | null): PartnerEarnings {
  const currencies = response?.currencies ?? [];
  return {
    volume: currencies.map(c => ({ currency: c.currencyCode, total: c.volume })),
    estimatedFee: currencies
      .filter(c => c.estimatedFee > 0)
      .map(c => ({ currency: c.currencyCode, total: c.estimatedFee })),
    dealsWithoutTerm: response?.dealsWithoutTerm ?? 0,
  };
}
